import { AnyAction, createListenerMiddleware, ThunkDispatch } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { AppState } from '../types/AppState';
import {
  setOrderDealer,
  setOrderOwner,
  setOrderStatus,
  openLeadDetailDialog,
  deleteLead,
  deleteLeadSuccess,
  setOrderStatusSuccess,
  setOrderOwnerSuccess,
  setOrderDealerSuccess,
} from '../ducks/leadOrderSlice';
import { leadApi } from '../services/leadApi';
import { PortalInfoSchema } from '../constants/PortalInfoSchema';
import { deletedLeadStatus } from '../constants/OrderStatus';
import { getSiteFromClientId } from '../utils/clientIdUtils';
import { OrderStatus } from '../types/OrderStatus';
import { openDialog, setLead } from '../ducks/dialogSlice';
import { openNotificationDialog } from '../ducks/notificationDialog';
import { Dialogs } from '../constants/Dialogs';
import { isIdeaRoomUser } from '../utils/userUtils';

export const orderListener = createListenerMiddleware<AppState>();

const sendLeadAssignementEmail = async (
  groupId: string,
  clientId: string,
  uuid: string,
  email: string,
  name: string,
  status: OrderStatus | string,
  site: string,
  logoUrl: string,
  selectedTextColor: string,
  dispatch: ThunkDispatch<AppState, unknown, AnyAction>,
) => {
  await dispatch(
    leadApi.endpoints.assignLead.initiate({
      groupId,
      clientId,
      uuid,
      emailAddress: email,
      status,
      site,
      logoUrl,
      buttonColor: selectedTextColor,
    }),
  );

  toast.success(`A lead notification has been sent to ${name}.`);
};

orderListener.startListening({
  actionCreator: setOrderDealer,
  effect: async (action, { getState, dispatch }) => {
    const { clientId, uuid, orderStatus, orderDealer } = action.payload;
    const { emailAddress: email, name } = orderDealer;
    const {
      currentUser: { group: { groupId = '', settings, configurators = [] } = { settings: undefined } },
      viewer: { theme: { logoUrl = '', selectedTextColor = '' } = {} },
    } = getState() as AppState;

    // Update portal info
    await dispatch(
      leadApi.endpoints.updatePortalInfo.initiate({
        clientId,
        uuid,
        key: PortalInfoSchema.OrderDealer,
        value: orderDealer,
      }),
    );

    // Send notification email if configured
    if (settings && settings.dealerNotifications && email) {
      const site = getSiteFromClientId(clientId, configurators) || '';
      await sendLeadAssignementEmail(
        groupId,
        clientId,
        uuid,
        email,
        name,
        orderStatus,
        site,
        logoUrl,
        selectedTextColor,
        dispatch,
      );
      toast.success(`A lead notification has been sent to ${name}.`);
    }
    dispatch(setOrderDealerSuccess({ clientId, uuid, orderDealer }));
  },
});

orderListener.startListening({
  actionCreator: setOrderOwner,
  effect: async (action, { getState, dispatch }) => {
    const { clientId, uuid, orderOwner, orderStatus } = action.payload;
    const { email, name } = orderOwner;
    // Send an email to the owner notifying them of the new lead
    const {
      currentUser: { group: { groupId = '', settings, configurators = [] } = { settings: undefined } },
      viewer: { theme: { logoUrl = '', selectedTextColor = '' } = {} },
    } = getState() as AppState;

    await dispatch(
      leadApi.endpoints.updatePortalInfo.initiate({
        clientId,
        uuid,
        key: PortalInfoSchema.OrderOwner,
        value: orderOwner,
      }),
    );

    // Send notification email if configured
    if (settings && settings.salesRepNotifications && email) {
      const site = getSiteFromClientId(clientId, configurators) || '';
      await sendLeadAssignementEmail(
        groupId,
        clientId,
        uuid,
        email,
        name,
        orderStatus,
        site,
        logoUrl,
        selectedTextColor,
        dispatch,
      );
      toast.success(`A lead notification has been sent to ${name}.`);
    }
    dispatch(setOrderOwnerSuccess({ clientId, uuid, orderOwner }));
  },
});

orderListener.startListening({
  actionCreator: setOrderStatus,
  effect: async (action, { dispatch }) => {
    const { clientId, uuid, orderStatus } = action.payload;

    // Update portal info
    await dispatch(
      leadApi.endpoints.updatePortalInfo.initiate({
        clientId,
        uuid,
        key: PortalInfoSchema.OrderStatus,
        value: orderStatus,
      }),
    );
  },
});

orderListener.startListening({
  actionCreator: deleteLead,
  effect: async (_, { getState, dispatch }) => {
    const state = getState() as AppState;
    const currentLead = state.dialog.lead;
    const currentUser = state.currentUser.user;

    if (currentLead) {
      // Update portal info to mark the lead as deleted
      await dispatch(
        leadApi.endpoints.updatePortalInfo.initiate({
          clientId: currentLead.clientId,
          uuid: currentLead.uuid,
          key: PortalInfoSchema.OrderStatus,
          value: deletedLeadStatus,
        }),
      );

      if (isIdeaRoomUser(currentUser)) {
        // If an Idearoom Admin, return as if a regular status change
        await dispatch(
          setOrderStatusSuccess({
            clientId: currentLead.clientId,
            uuid: currentLead.uuid,
            orderStatus: deletedLeadStatus,
          }),
        );
      } else {
        // Update the leads to remove this lead from the list
        await dispatch(deleteLeadSuccess({ uuid: currentLead.uuid }));
      }
    }
  },
});

orderListener.startListening({
  actionCreator: openLeadDetailDialog,
  effect: async (action, { dispatch }) => {
    const { leads, uuid } = action.payload;

    // Check if an lead with the uuid exists in the list of leads
    // if so open the lead details dialog with that lead
    // otherwise open a snackbar saying the lead was not found
    const lead = leads.find((l) => l.uuid === uuid);
    if (lead) {
      dispatch(setLead(lead));
      dispatch(openDialog({ dialog: Dialogs.LeadDetail }));
    } else {
      const notificationContent = `
      <p>The building you are trying to view is not available to you in SalesView.</p>
      It may have been deleted, or you may not have access.
    `;
      dispatch(openNotificationDialog('', notificationContent));
      dispatch(openDialog({ dialog: Dialogs.Notification }));
    }
  },
});
